<template>
  <div class="chart">
    <vs-loader :visible="showLoader" />
    <template v-if="chartData">
      <TopChart
        :chartData="chartData"
        :options="chartOptions"
        :styles="{
          width: '95%',
          margin: 'auto'
        }"
      />
    </template>
  </div>
</template>
<script>
import TopChart from './TopChart'
import tooltipConfig from '@/misc/chartTooltip'

export default {
  components: {
    TopChart
  },
  props: {
    coverageData: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      chartData: null
    }
  },
  computed: {
    coverageRate () {
      return this.coverageData['coverage-rate']
    },
    pageViews () {
      return this.coverageData['item-page-views']
    },
    vsPageViews () {
      return this.coverageData['vs-item-page-views']
    },
    showLoader () {
      return !!this.coverageRate.isLoading || !!this.pageViews.isLoading || !!this.vsPageViews.isLoading
    },
    isChartAvailable () {
      if (!this.coverageRate.data || !this.pageViews.data || !this.vsPageViews.data) {
        return false
      }
      return true
    },
    chartOptions () {
      const numberFormat = this.$options.filters.numberFormat
      return {
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        elements: { point: { radius: 0.05 } },
        scales: {
          xAxes: [{
            stacked: true,
            gridLines: {
              display: false
            }
          }],
          yAxes: [{
            stacked: true,
            position: 'left',
            id: 'line-axis',
            gridLines: {
              drawBorder: false,
              display: true
            },
            ticks: {
              min: 0,
              max: 100,
              callback: value => `${value}  `
            }
          }, {
            stacked: true,
            position: 'right',
            id: 'bar-axis',
            gridLines: {
              drawBorder: false,
              display: false
            },
            ticks: {
              callback: value => Number(value) / 1000 + 'K'
            }
          }]
        },
        tooltips: {
          ...tooltipConfig,
          callbacks: {
            title: function (tooltipItem, data) {
              return `/${tooltipItem[0].label}`
            },
            label: function (tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex]
              return ` ${dataset.label}: `
            },
            labelTextColor: function (tooltipItem, chart) {
              return tooltipItem.datasetIndex === 1 ? '#EC386E' : '#16C6B9'
            },
            afterLabel: function (tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex]
              const index = tooltipItem.index
              const value = dataset.data[index]
              return `${numberFormat(value)}${dataset.name === 'coverage-rate' ? '%' : ''}`
            }
          }

        }
      }
    }
  },
  watch: {
    coverageData () {
      this.setChartData()
    },
    isChartAvailable (changedValue) {
      this.setChartData()
    }
  },
  created () {
    this.setChartData()
  },
  methods: {
    async setChartData () {
      if (this.isChartAvailable) {
        this.chartData = await this.dataCollection()
      }
    },
    dataCollection () {
      const { DateString } = this.$options.filters
      let coverageData = {
        labels: this.coverageRate.data.map(data => DateString(data.date, 'MM/DD')),
        datasets: [
          {
            type: 'bar',
            label: 'Page Views With VS',
            name: 'page-views-with-vs',
            data: this.vsPageViews.data.map(data => data.val),
            backgroundColor: 'rgba(34,213,200, 0.2)',
            hoverBackgroundColor: 'rgba(34,213,200, 0.76)',
            yAxisID: 'bar-axis',
            borderColor: 'rgba(34,213,200, 0.76)'
          },
          {
            type: 'bar',
            label: 'Page Views Without VS',
            name: 'page-views-without-vs',
            data: this.pageViews.data.map((data, index) => data.val - this.vsPageViews.data[index].val),
            backgroundColor: 'rgba(244,98,142, 0.2)',
            hoverBackgroundColor: 'rgba(244,98,142, 0.76)',
            yAxisID: 'bar-axis',
            borderColor: 'rgba(244,98,142, 0.76)'
          },
          {
            type: 'line',
            label: `Coverage Rate`,
            name: 'coverage-rate',
            data: this.coverageRate.data.map(data => Math.floor(data.val * 100)),
            backgroundColor: 'rgba(0, 0, 0, 0)',
            hoverBackgroundColor: 'rgb(22,198,185)',
            borderColor: 'rgb(22,198,185)',
            pointHoverBackgroundColor: 'rgb(22,198,185)',
            yAxisID: 'line-axis',
            borderWidth: 2,
            pointHoverRadius: 3
          }
        ]
      }
      return Promise.resolve(coverageData)
    }
  }
}
</script>

<style lang="scss" scoped>
.chart {
  position: relative;
  min-height: 430px;
  padding: 15px 0;
  border-top: $border-light;
}
</style>
